import type { Cents, InvoiceStatus } from './types';

/**
 * Invoice lifecycle, mirroring the backend's state machine so the UI can
 * predict what the server will do rather than guessing from a status string.
 */
const TRANSITIONS: Record<InvoiceStatus, readonly InvoiceStatus[]> = {
  OPEN: ['PARTIALLY_PAID', 'PAID', 'CANCELLED'],
  PARTIALLY_PAID: ['OPEN', 'PARTIALLY_PAID', 'PAID', 'CANCELLED'],
  // Voiding a receipt reverses allocations, so PAID is not terminal.
  PAID: ['OPEN', 'PARTIALLY_PAID', 'PAID'],
  CANCELLED: [],
};

export function canTransition(from: InvoiceStatus, to: InvoiceStatus): boolean {
  return from === to || TRANSITIONS[from].includes(to);
}

export function isCollectable(status: InvoiceStatus): boolean {
  return status === 'OPEN' || status === 'PARTIALLY_PAID';
}

export function isSettled(status: InvoiceStatus): boolean {
  return status === 'PAID' || status === 'CANCELLED';
}

export function outstandingCents(amountCents: Cents, allocatedCents: Cents): Cents {
  return Math.max(0, amountCents - allocatedCents);
}

/**
 * Editable at any stage except cancelled — the amount just can't drop below
 * `allocatedCents`, which the edit form itself enforces.
 */
export function canEditInvoice(status: InvoiceStatus): boolean {
  return status !== 'CANCELLED';
}

export function canCancelInvoice(status: InvoiceStatus, allocatedCents: Cents): boolean {
  return status !== 'CANCELLED' && allocatedCents === 0;
}

/** Badge colour per status, so the whole app agrees on what green means. */
export const STATUS_TONE: Record<InvoiceStatus, 'neutral' | 'warning' | 'success' | 'danger'> = {
  OPEN: 'neutral',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  CANCELLED: 'danger',
};
